import { useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { AlertTriangle, ChevronRight } from 'lucide-react';
import { Checkbox, TableCell } from '@/components/ui';
import type {
  AdminDecorationMethod,
  AdminProductPriceRow,
  OfferedMethod,
  PriceRowParams,
  PriceState,
  ReleaseChannel,
  SalesPlatform,
  SellerCostReason,
} from '@/api/admin';
import { getErrorMessage } from '@/api';
import { useSetProductChannelRelease } from '@/hooks/useAdminProducts';
import { formatAdminDate } from '@/lib/format-date';
import { formatPriceInput } from '@/lib/price-input';
import { proxyStorageUrl } from '@/lib/product-transformer';
import { cn } from '@/lib/utils';
import {
  OfferMethodButton,
  WithdrawMethodButton,
} from './MethodWithdrawDialog';
import { PriceCell, type PriceCellState } from './PriceCell';
import { PRODUCT_CLASS_LABEL } from './ProductCarryOverPanel';

/**
 * Sticky classes of the three leading cells (R13b): selection, active
 * switch and product stay visible while the decoration columns scroll.
 * The offsets add up the fixed widths of the cells before them.
 */
export const STICKY_CELL = {
  select: 'sticky left-0 z-10 w-10 bg-paper-2 px-2 group-hover:bg-paper-3',
  active: 'sticky left-10 z-10 w-14 bg-paper-2 px-2 group-hover:bg-paper-3',
  product:
    'sticky left-24 z-10 min-w-[16rem] bg-paper-2 group-hover:bg-paper-3 shadow-[inset_-1px_0_0_var(--line)]',
} as const;

const SELLER_COST_REASON: Record<SellerCostReason, string> = {
  no_position: 'no active position',
  blank_unknown: 'blank cost unknown',
  no_method: 'no decoration method',
  method_unpriced: 'default method unpriced',
};

export interface CellView {
  value: string | null;
  placeholder: string | null;
  state: PriceCellState;
  hint?: string;
  hintTitle?: string;
}

/**
 * How a product-level price column shows one of the API's price states
 * (R13): the product's own value is plain; variant values are grey in
 * the placeholder; the import placeholder and a missing price are orange.
 */
export function priceStateView(
  price: PriceState,
  options: { variantCount?: number } = {}
): CellView {
  switch (price.kind) {
    case 'product': {
      const variantCount = options.variantCount ?? 0;
      // KD21: every sellable variant carries its own value, so the product
      // value is kept but not the one sellers are charged with.
      const overridden = variantCount > 0 && price.count === variantCount;
      return {
        value: price.value,
        placeholder: null,
        state: overridden ? 'muted' : 'default',
        hint: overridden ? `${price.count} sizes have own price` : undefined,
        hintTitle: overridden
          ? `All ${price.count} sellable variants carry their own price (${price.min} to ${price.max}); the product value is not used`
          : undefined,
      };
    }
    case 'uniform':
      return {
        value: null,
        placeholder: price.value,
        state: 'muted',
        hint: 'from variants',
        hintTitle: `${price.count} variants carry ${price.value}; the product has no value of its own`,
      };
    case 'mixed':
      return {
        value: null,
        placeholder: `${price.min}–${price.max}`,
        state: 'muted',
        hint: 'varies',
        hintTitle: `${price.count} variants range from ${price.min} to ${price.max}`,
      };
    case 'placeholder':
      return {
        value: null,
        placeholder: '999.99',
        state: 'warning',
        hint: 'placeholder',
        hintTitle:
          'Import placeholder 999.99 on every variant, not a real price',
      };
    case 'none':
      return {
        value: null,
        placeholder: null,
        state: 'warning',
        hint: 'missing',
      };
  }
}

/**
 * How a decoration cell shows an offered method: the override is the
 * value, the platform's base price the placeholder; a method without
 * either is orange, one not offered on every active position gets a hint.
 */
export function methodCellView(
  offered: OfferedMethod,
  platform: SalesPlatform,
  basePrice: string | null
): CellView {
  const value = offered.overrides[platform];
  if (!offered.priced) {
    return {
      value,
      placeholder: basePrice,
      state: 'warning',
      hint: 'no price',
      hintTitle: 'Neither a base price nor an override exists on this platform',
    };
  }
  return {
    value,
    placeholder: basePrice,
    state: value === null ? 'muted' : 'default',
    ...(!offered.onAllPositions && {
      hint: 'not on all positions',
      hintTitle: 'Offered on some active positions only',
    }),
  };
}

/** Thumbnail, name with the detail link, the meta line and the R1a warning. */
export function ProductCell({
  row,
  categoryName,
  manufacturerName,
}: {
  row: AdminProductPriceRow;
  categoryName?: string;
  manufacturerName?: string;
}) {
  const imageUrl = proxyStorageUrl(row.thumbnailImageUrl);
  const meta = [manufacturerName, categoryName]
    .filter((part): part is string => Boolean(part))
    .join(' · ');

  return (
    <TableCell className={STICKY_CELL.product}>
      <div className="flex items-center gap-3">
        <Link
          to={`/admin/products/${row.id}`}
          tabIndex={-1}
          aria-hidden="true"
          className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-control bg-paper ring-1 ring-line"
        >
          {imageUrl ? (
            <img
              src={imageUrl}
              alt=""
              className="h-full w-full object-contain"
            />
          ) : (
            <span className="text-[10px] text-mute">—</span>
          )}
        </Link>
        {/* Bounded so a long name wraps instead of widening the column. */}
        <div className="min-w-0 max-w-[15rem]">
          <div className="flex items-start gap-1.5">
            <Link
              to={`/admin/products/${row.id}`}
              className="break-words font-semibold leading-snug text-ink underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              {row.name}
            </Link>
            {row.decorationPriceMissing && (
              <span
                role="img"
                aria-label="No priced decoration method"
                title="No priced decoration method"
                className="inline-flex shrink-0 text-warning"
              >
                <AlertTriangle className="h-4 w-4" aria-hidden="true" />
              </span>
            )}
          </div>
          {/* The SKU gets its own line so the truncated meta never hides it. */}
          {row.artNumber && (
            <p
              className="truncate font-mono text-xs text-ink-2"
              title={row.artNumber}
              data-testid="product-sku"
            >
              {row.artNumber}
            </p>
          )}
          <p className="truncate text-xs text-mute" title={meta || undefined}>
            {meta || '—'}
          </p>
        </div>
      </div>
    </TableCell>
  );
}

/**
 * The Phoenix main product the variant SKUs belong to, as the last
 * `catalog:sync-phoenix-mapping` run saw it: its id and status, a
 * warning when not all of our SKUs exist there, orange without mapping.
 */
export function PhoenixCell({ row }: { row: AdminProductPriceRow }) {
  const phoenix = row.phoenix;
  if (!phoenix) {
    return (
      <TableCell
        className="whitespace-nowrap text-xs text-mute"
        data-testid="phoenix"
        title="Phoenix mapping not checked yet"
      >
        not checked
      </TableCell>
    );
  }

  const checked = `checked ${formatAdminDate(phoenix.checkedAt)}`;
  if (phoenix.productId === null) {
    return (
      <TableCell
        className="whitespace-nowrap text-xs text-warning"
        data-testid="phoenix"
        title={`None of the ${phoenix.comparedVariants} variant SKUs and not the article number exist in Phoenix; ${checked}`}
      >
        no mapping
      </TableCell>
    );
  }

  const partial = phoenix.mappedVariants < phoenix.comparedVariants;
  const inactive = phoenix.status !== 'ACTIVE';
  return (
    <TableCell
      className="whitespace-nowrap"
      data-testid="phoenix"
      title={`Phoenix product ${phoenix.productId}, ${phoenix.status ?? 'status unknown'}; ${phoenix.mappedVariants} of ${phoenix.comparedVariants} variant SKUs found; ${checked}`}
    >
      <span className="block font-mono text-sm text-ink">
        #{phoenix.productId}
      </span>
      <span
        className={cn(
          'block text-[11px] leading-tight',
          inactive ? 'text-warning' : 'text-mute'
        )}
      >
        {inactive ? (phoenix.status ?? 'unknown').toLowerCase() : 'active'}
      </span>
      {partial && (
        <span className="block text-[11px] leading-tight text-warning">
          {phoenix.mappedVariants}/{phoenix.comparedVariants} SKUs
        </span>
      )}
    </TableCell>
  );
}

/**
 * The end-of-life mark: a checkbox, with the date it was set underneath.
 * An admin note only; it changes nothing for sellers or designs.
 */
export function EndOfLifeCell({
  row,
  disabled,
  onChange,
}: {
  row: AdminProductPriceRow;
  disabled: boolean;
  onChange: (endOfLife: boolean) => void;
}) {
  const marked = row.endOfLifeAt !== null;
  return (
    <TableCell
      className="whitespace-nowrap text-center"
      data-testid="end-of-life"
      title={
        marked
          ? `End of life since ${formatAdminDate(row.endOfLifeAt)}`
          : 'Not marked as end of life'
      }
    >
      <Checkbox
        checked={marked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        aria-label={`${row.name} end of life`}
      />
      {marked && (
        <span className="block text-[11px] leading-tight text-warning">
          {formatAdminDate(row.endOfLifeAt)}
        </span>
      )}
    </TableCell>
  );
}

/** Hint of a release checkbox on an inactive product. */
export const INACTIVE_RELEASE_HINT =
  'Product is inactive and not visible on any channel';

/** "Shirtee Cloud (SHICL)": keeps SHI and SHICL apart in titles. */
export function releaseChannelTitle(channel: ReleaseChannel): string {
  return `${channel.name} (${channel.key})`;
}

/**
 * The release of a product on one admin channel (R15, R16): a checkbox
 * that switches at once and falls back with a toast when the write
 * fails. A second click while the write runs does nothing. On an inactive
 * product the box stays focusable and shows its state, but is
 * `aria-disabled`, explains why and ignores clicks and keys.
 */
export function ChannelReleaseCell({
  row,
  channel,
  priceRowParams,
}: {
  row: AdminProductPriceRow;
  channel: ReleaseChannel;
  priceRowParams: PriceRowParams;
}) {
  const hintId = useId();
  const setRelease = useSetProductChannelRelease(priceRowParams);
  /** The requested state while the write runs; null shows the row's. */
  const [optimistic, setOptimistic] = useState<boolean | null>(null);
  const pending = useRef(false);

  const released = optimistic ?? row.channels[channel.key] ?? false;
  const blocked = !row.isActive;

  const handleChange = async (next: boolean) => {
    if (blocked || pending.current) return;
    pending.current = true;
    setOptimistic(next);
    try {
      await setRelease.mutateAsync({
        productId: row.id,
        channelKey: channel.key,
        released: next,
      });
    } catch (error) {
      toast.error(
        `Could not update "${row.name}" on ${channel.name}: ${getErrorMessage(error)}`,
        { position: 'bottom-right' }
      );
    } finally {
      pending.current = false;
      setOptimistic(null);
    }
  };

  return (
    <TableCell
      className="text-center"
      data-testid={`channel-${channel.key}`}
      title={
        blocked
          ? INACTIVE_RELEASE_HINT
          : `${releaseChannelTitle(channel)}: ${released ? 'released' : 'not released'}`
      }
    >
      <Checkbox
        checked={released}
        aria-disabled={blocked || undefined}
        aria-busy={optimistic !== null || undefined}
        aria-describedby={blocked ? hintId : undefined}
        className={cn(blocked && 'cursor-not-allowed opacity-60')}
        onChange={(event) => void handleChange(event.target.checked)}
        aria-label={`${row.name}: released on ${channel.name}`}
      />
      {blocked && (
        <span id={hintId} className="sr-only">
          {INACTIVE_RELEASE_HINT}
        </span>
      )}
    </TableCell>
  );
}

/**
 * Class and percent of the product's carry-over, read only: a foreign design
 * lands on this product by these values (areas may still set exceptions).
 */
export function CarryOverCell({ row }: { row: AdminProductPriceRow }) {
  const parts = [
    row.productClass ? PRODUCT_CLASS_LABEL[row.productClass] : null,
    row.carryOverPercent != null ? `${row.carryOverPercent} %` : null,
  ].filter((part): part is string => part !== null);
  return (
    <TableCell className="whitespace-nowrap">
      <span
        className={parts.length > 0 ? 'text-ink' : 'text-mute'}
        title={
          parts.length > 0
            ? undefined
            : 'No class, default rules (class rules off)'
        }
      >
        {parts.length > 0 ? parts.join(' · ') : '—'}
      </span>
    </TableCell>
  );
}

/** The variant count as the toggle of the size row. */
export function VariantsCell({
  row,
  expanded,
  sizeRowId,
  onToggle,
}: {
  row: AdminProductPriceRow;
  expanded: boolean;
  sizeRowId: string;
  onToggle: () => void;
}) {
  return (
    <TableCell className="whitespace-nowrap">
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={sizeRowId}
        aria-label={`${row.variantCount} variants of ${row.name}, show sizes`}
        onClick={onToggle}
        className="inline-flex items-center gap-1 rounded-control px-1.5 py-1 font-mono text-sm text-ink hover:bg-paper-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <ChevronRight
          className={cn(
            'h-4 w-4 text-mute transition-transform',
            expanded && 'rotate-90'
          )}
          aria-hidden="true"
        />
        {row.variantCount}
      </button>
    </TableCell>
  );
}

/** The computed seller cost, or "unknown" with the API's reason. */
export function SellerCostCell({ row }: { row: AdminProductPriceRow }) {
  return (
    <TableCell className="text-right font-mono" data-testid="seller-cost">
      {row.sellerCost !== null ? (
        <span className="whitespace-nowrap">
          {formatPriceInput(row.sellerCost)}
        </span>
      ) : (
        <span className="flex flex-col items-end text-mute">
          <span>unknown</span>
          {row.sellerCostReason && (
            <span className="max-w-[7rem] font-sans text-[11px] leading-tight">
              {SELLER_COST_REASON[row.sellerCostReason]}
            </span>
          )}
        </span>
      )}
    </TableCell>
  );
}

/**
 * One decoration column of a row: the override field with the withdraw
 * button for an offered method, or the offer button for one the product
 * does not offer.
 */
export function MethodCell({
  row,
  method,
  offered,
  platform,
  basePrice,
  priceRowParams,
  onCommit,
}: {
  row: AdminProductPriceRow;
  method: AdminDecorationMethod;
  offered: OfferedMethod | undefined;
  platform: SalesPlatform;
  basePrice: string | null;
  priceRowParams: PriceRowParams;
  onCommit: (value: string | null) => Promise<void>;
}) {
  if (!offered) {
    return (
      <TableCell
        data-slot="offer"
        data-method-id={method.id}
        className="text-center"
      >
        <OfferMethodButton
          productId={row.id}
          productName={row.name}
          methodId={method.id}
          methodName={method.name}
          priceRowParams={priceRowParams}
        />
      </TableCell>
    );
  }
  return (
    <TableCell data-method-id={method.id}>
      <PriceCell
        {...methodCellView(offered, platform, basePrice)}
        aria-label={`${row.name}, ${method.name}`}
        onCommit={onCommit}
        trailing={
          <WithdrawMethodButton
            productId={row.id}
            productName={row.name}
            method={offered}
            priceRowParams={priceRowParams}
          />
        }
      />
    </TableCell>
  );
}

/** Date and user of the newest price audit entry, or a dash. */
export function LastPriceChangeCell({ row }: { row: AdminProductPriceRow }) {
  return (
    <TableCell className="whitespace-nowrap text-xs text-ink-2">
      {row.lastPriceChange ? (
        <>
          {formatAdminDate(row.lastPriceChange.at)}
          <br />
          <span className="text-mute">
            {row.lastPriceChange.userName ?? 'Unknown user'}
          </span>
        </>
      ) : (
        '–'
      )}
    </TableCell>
  );
}
